import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { FormEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_ERROR } from '../components/Notifications/Notification';
import { getS3Client, INVOICE_DATA_BUCKET_NAME } from '../core/s3';
import { addNotification } from '../core/store/notificationsSlice';
import { useAppDispatch, useAppSelector } from '../hooks/useReduxToolkit';
import * as Sentry from '@sentry/browser';
import { getManualUploadFiles } from '../core/api';
import { UploadedFile } from '../core/types';
import { sleep } from '../core/helpers';
import { useQuery, useQueryClient } from 'react-query';

export const ACCEPTED_EXTS = ['xls', 'xlsx', 'csv'];

export enum ManualUploadFileStatusEnum {
  CREATED,
  IMPORTING,
  READY,
  VALIDATED,
}

export const useManualUpload = (accept = ACCEPTED_EXTS) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isUploading, setIsUploading] = useState<{
    [key: string]: boolean;
  }>({});

  const [filesToUpload, setFilesToUpload] = useState<{ [key: string]: File[] }>(
    {}
  );

  const controller = useRef(new AbortController());

  const user = useAppSelector((s) => s.user.user);

  const activeCompanyId = useAppSelector(
    (s) => s.user.activeCompany?.company_id
  );

  const { data: uploadedFiles, isLoading } = useQuery<UploadedFile[]>(
    ['SellerManualUploadFiles', user!.Id, activeCompanyId],
    () => getManualUploadFiles(activeCompanyId!)
  );

  const s3ClientRef = useRef<S3Client | null>(null);
  const uploadErrorRef = useRef<{
    [key: string]: boolean;
  }>({});

  const onSelectFiles = (files: File[] | null) => {
    const filesForUploading = files?.filter((f) => {
      const parts = f.name.split('.');
      const ext = parts[parts.length - 1];
      return accept.includes((ext || '').toLowerCase());
    });

    if (!files || !filesForUploading) {
      return;
    }

    if (files.length > filesForUploading.length) {
      dispatch(
        addNotification({
          text: t('You can upload multiple CSV, XLS, XLSX files.'),
          type: 'error',
        })
      );
      return;
    }

    setFilesToUpload((prev) => {
      return {
        ...prev,
        [activeCompanyId!]: [
          ...filesForUploading.map((f) => {
            const blob = f.slice(0, f.size, f.type);
            const nameParts = f.name.split('.');

            return new File(
              [blob],
              `${nameParts.slice(0, -1).join('.')}_${Date.now()}.${
                nameParts[nameParts.length - 1]
              }`,
              { type: f.type }
            );
          }),
          ...(prev[activeCompanyId!] || []).filter((f) => {
            const fileToReplace = filesForUploading.find(
              (newFile) => newFile.name === f.name
            );
            if (fileToReplace) {
              return f.name !== fileToReplace.name;
            }
            return true;
          }),
        ],
      };
    });
  };

  const uploadFile = async (file: File) => {
    try {
      await s3ClientRef.current!.send(
        new PutObjectCommand({
          Bucket: INVOICE_DATA_BUCKET_NAME,
          Key: `${activeCompanyId}/${file.name}`,
          Body: file,
          Metadata: {
            userId: user!.Id,
          },
        })
      );
    } catch (error) {
      uploadErrorRef.current = {
        ...uploadErrorRef.current,
        [activeCompanyId!]: true,
      };
      Sentry.captureException(error);

      dispatch(
        addNotification({
          text: t('Error has occurred while uploading file *filename*').replace(
            '*filename*',
            file.name
          ),
          type: 'error',
        })
      );
    }
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: true }));
    uploadErrorRef.current = {
      ...uploadErrorRef.current,
      [activeCompanyId!]: false,
    };

    try {
      s3ClientRef.current = await getS3Client();
      await Promise.all(
        (filesToUpload[activeCompanyId!] || []).map((f) => uploadFile(f))
      );
      s3ClientRef.current.destroy();

      if (!uploadErrorRef.current[activeCompanyId!]) {
        setTimeout(async () => {
          try {
            const res = await getManualUploadFiles(activeCompanyId!);
            queryClient.setQueryData<UploadedFile[]>(
              ['SellerManualUploadFiles', user!.Id, activeCompanyId],
              res
            );
            if (
              res.every((f) => f.status !== ManualUploadFileStatusEnum.CREATED)
            ) {
              setFilesToUpload((prev) => ({
                ...prev,
                [activeCompanyId!]: [],
              }));
              setIsUploading((prev) => ({
                ...prev,
                [activeCompanyId!]: false,
              }));
              dispatch(
                addNotification({
                  type: 'success',
                  text: t('Manual turnover data has been processed'),
                })
              );
            }
          } catch (error) {
            console.error(error);
          }
        }, 5000);
      }
    } catch (err) {
      setIsUploading((prev) => ({ ...prev, [activeCompanyId!]: false }));

      dispatch(
        addNotification({
          text: t(DEFAULT_ERROR),
          type: 'error',
        })
      );
    }
  };

  const onDeleteFile = (name: string) => () => {
    setFilesToUpload((prev) => ({
      ...prev,
      [activeCompanyId!]: (prev[activeCompanyId!] || []).filter(
        (f) => f.name !== name
      ),
    }));
  };

  useEffect(() => {
    if (!!uploadedFiles?.length) {
      controller.current.abort();
      controller.current = new AbortController();
      if (
        uploadedFiles.some(
          (f) => f.status === ManualUploadFileStatusEnum.CREATED
        )
      ) {
        pollFiles(
          activeCompanyId!,
          controller.current,
          (files: UploadedFile[]) => {
            queryClient.setQueryData(
              ['SellerManualUploadFiles', user!.Id, activeCompanyId],
              files
            );
            setFilesToUpload((prev) => ({
              ...prev,
              [activeCompanyId!]: (prev[activeCompanyId!] || []).filter(
                (f) => !files.some((file) => f.name === file.name)
              ),
            }));

            if (
              files.every(
                (f) => f.status !== ManualUploadFileStatusEnum.CREATED
              )
            ) {
              setIsUploading((prev) => ({
                ...prev,
                [activeCompanyId!]: false,
              }));
              dispatch(
                addNotification({
                  type: 'success',
                  text: t('Manual turnover data has been processed'),
                })
              );
            }
          }
        );
      }
    }

    // eslint-disable-next-line
  }, [activeCompanyId, uploadedFiles]);

  return {
    filesToUpload: filesToUpload[activeCompanyId!] || [],
    onSelectFiles,
    onDeleteFile,
    onSubmit,
    isLoading,
    controller: controller.current,
    uploadedFiles: uploadedFiles || [],
    isUploading: isUploading[activeCompanyId!] || false,
  };
};

const pollFiles = async (
  company_id: string,
  controller: AbortController,
  cb: (files: UploadedFile[]) => void,
  count: number = 1
) => {
  try {
    const res = await getManualUploadFiles(company_id, controller);

    if (res.some((f) => f.status === ManualUploadFileStatusEnum.CREATED)) {
      await sleep(5000);
      pollFiles(company_id, controller, cb);
    } else {
      cb(res);
    }
  } catch {
    if (count < 3) {
      await sleep(5000);
      try {
        pollFiles(company_id, controller, cb, ++count);
      } catch {}
    }
  }
};
